import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import ReportSaleLink from '../../components/reports/ReportSaleLink';
import ReportStockItemLink from '../../components/reports/ReportStockItemLink';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { toReportExportAction } from '../../components/ReportExportButton';
import { reportsService } from '../../services/reportsService';
import { clientsService } from '../../services/salesService';
import { productsService } from '../../services/productionService';
import { downloadReportCsv } from '../../utils/reportExport';
import FilterDrawer, { toFilterAction } from '../../components/FilterDrawer';
import useReportFilters from '../../utils/useReportFilters';
import { Input } from '../../components/ui/input';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { Autocomplete } from '../../components/ui/autocomplete';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn, formatBRL, formatDateBR, formatQuantity, localDateISO } from '../../lib/utils';

const ORIGIN_OPTIONS = [
    { value: 'internal', label: 'Interna' },
    { value: 'external', label: 'Externa' },
];

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Orçado' },
    { value: 'approved', label: 'Aprovado' },
    { value: 'partial', label: 'Parcial' },
    { value: 'completed', label: 'Concluído' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'expired', label: 'Expirado' },
];

const selectClass = 'h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500';

function monthStartISO() {
    const now = new Date();
    return localDateISO(new Date(now.getFullYear(), now.getMonth(), 1));
}

function SaleBacklogReport() {
    usePageTitle('Carteira de pedidos');
    const { toastApiError } = useToast();

    const filters = useReportFilters(() => ({
        period: { from: monthStartISO(), to: localDateISO() },
        clientId: null,
        documentNumber: '',
        productId: null,
        origin: '',
        status: '',
    }));

    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [cl, pr] = await Promise.all([
                    clientsService.list({ per_page: 1000 }),
                    productsService.list({ per_page: 1000 }),
                ]);
                if (cancelled) return;
                const clRows = cl.data?.data || cl.data;
                const prRows = pr.data?.data || pr.data;
                setClients(Array.isArray(clRows) ? clRows : []);
                setProducts(Array.isArray(prRows) ? prRows : []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const buildParams = useCallback(() => {
        const f = filters.applied;
        return {
            date_from: f.period.from || undefined,
            date_to: f.period.to || undefined,
            client_id: f.clientId || undefined,
            document_number: f.documentNumber || undefined,
            product_id: f.productId || undefined,
            origin: f.origin || undefined,
            status: f.status || undefined,
        };
    }, [filters.applied]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await reportsService.saleBacklog(buildParams());
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o relatório' });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildParams]);

    useEffect(() => {
        load();
    }, [load]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadReportCsv(reportsService.saleBacklogCsv(buildParams()), 'carteira-pedidos.csv');
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao exportar', fallback: 'Não foi possível exportar o relatório' });
        } finally {
            setExporting(false);
        }
    };

    const thBase = 'h-[56px] text-sm font-medium text-gray-500 text-center p-0';
    const tdBase = 'h-[56px] text-sm text-gray-800 p-0';
    const tdEnd = 'justify-end tabular-nums text-right';
    const ROW_HEIGHT = 56;
    const thInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const tdInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center';

    const colWidths = [110, 120, 260, 110, 130, 160, 300, 130, 130, 140, 140, 130];
    const colLeft = [0, colWidths[0], colWidths[0] + colWidths[1]];

    const stickyTop = { position: 'sticky', top: 0, zIndex: 10, background: '#f9fafb' };
    const stickyTopWithLeft = (left, width) => ({ ...stickyTop, left, minWidth: width, width, zIndex: 11 });
    const stickyBody = (left, width) => ({ position: 'sticky', left, minWidth: width, width, background: '#fff', zIndex: 5 });
    const normalCol = (width) => ({ minWidth: width, width });
    const totalCols = 12;

    return (
        <AppLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col gap-0">
                <PageHeader
                    title="Carteira de pedidos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Relatórios' },
                    ]}
                    additionalActions={[toFilterAction({ count: filters.count, onClick: filters.openDrawer })]}
                    actions={[
                        toReportExportAction({ onExport: handleExport, disabled: loading, exporting }),
                    ]}
                />
                <div className="overflow-auto flex-1 min-h-0 -mx-4 md:-mx-8 -mb-8">
                    {loading && rows.length === 0 ? null : (
                        <table className="w-full caption-bottom text-sm">
                            <TableHeader className="border-b-0 bg-transparent">
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[0], colWidths[0])}>
                                        <div className={cn(thInner, 'report-first-col')}>Pedido</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[1], colWidths[1])}>
                                        <div className={thInner}>Status</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[2], colWidths[2])}>
                                        <div className={thInner}>Cliente</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[3]) }}>
                                        <div className={thInner}>Origem</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[4]) }}>
                                        <div className={thInner}>Data Cadastro</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[5]) }}>
                                        <div className={thInner}>Item</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[6]) }}>
                                        <div className={thInner}>Descrição</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[7]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Quantidade</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[11]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Entregue</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[8]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Saldo</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[9]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Valor Unitário</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[10]) }}>
                                        <div className={cn(thInner, 'justify-end border-r-2 border-r-gray-300')}>Valor Total</div>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow className="border-b-0 hover:bg-transparent">
                                        <TableCell colSpan={totalCols} className="text-center py-8 text-gray-500">
                                            Nenhum pedido no período
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row.id} className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[0], colWidths[0])}>
                                                <div className={cn(tdInner, 'report-first-col', 'tabular-nums')}>
                                                    <ReportSaleLink saleId={row.sale_id}>{row.document_number}</ReportSaleLink>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[1], colWidths[1])}>
                                                <div className={tdInner}>{row.status_label}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[2], colWidths[2])}>
                                                <div className={cn(tdInner, 'min-w-0')}>
                                                    <span className="truncate" title={row.client}>{row.client}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[3])}>
                                                <div className={tdInner}>{row.origin_label || '-'}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[4])}>
                                                <div className={cn(tdInner, 'tabular-nums')}>{formatDateBR(row.created_at)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[5])}>
                                                <div className={cn(tdInner, 'min-w-0')}>
                                                    <ReportStockItemLink productId={row.product_id} className="truncate block">
                                                        {row.item}
                                                    </ReportStockItemLink>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[6])}>
                                                <div className={cn(tdInner, 'min-w-0')}>
                                                    <span className="truncate" title={row.description}>{row.description || '-'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[7])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatQuantity(row.quantity)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[11])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatQuantity(row.delivered)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[8])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatQuantity(row.balance)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[9])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatBRL(row.unit_price)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[10])}>
                                                <div className={cn(tdInner, tdEnd, 'border-r-2 border-r-gray-300')}>{formatBRL(row.total)}</div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    )}
                </div>
            </div>
            <FilterDrawer open={filters.open} onClose={filters.close} onApply={filters.apply} onClear={filters.clear}>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Período de cadastro</label>
                    <DateRangePicker
                        className="w-full"
                        value={filters.draft.period}
                        onChange={(v) => filters.set('period', v)}
                        aria-label="Período de cadastro"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                    <Autocomplete
                        className="w-full"
                        options={clients}
                        getOptionLabel={(c) => c.name}
                        getOptionValue={(c) => Number(c.id)}
                        value={filters.draft.clientId}
                        onChange={(v) => filters.set('clientId', v)}
                        placeholder="Todos"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pedido</label>
                    <Input
                        value={filters.draft.documentNumber}
                        onChange={(e) => filters.set('documentNumber', e.target.value.replace(/\D/g, ''))}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                    <Autocomplete
                        className="w-full"
                        options={products}
                        getOptionLabel={(p) => `${p.code} - ${p.description || ''}`}
                        getOptionValue={(p) => Number(p.id)}
                        value={filters.draft.productId}
                        onChange={(v) => filters.set('productId', v)}
                        placeholder="Todos"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Origem</label>
                    <select
                        className={cn(selectClass, 'w-full')}
                        value={filters.draft.origin}
                        onChange={(e) => filters.set('origin', e.target.value)}
                    >
                        <option value="">Todas</option>
                        {ORIGIN_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        className={cn(selectClass, 'w-full')}
                        value={filters.draft.status}
                        onChange={(e) => filters.set('status', e.target.value)}
                    >
                        <option value="">Todos</option>
                        {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
            </FilterDrawer>
        </AppLayout>
    );
}

export default SaleBacklogReport;
